// src/common/ui/badges-status.js
// Badge and status icon utilities

/**
 * Get CSS class for a badge based on its label
 * Maps labels to three categories:
 * - verified (green): supports, partial, quote-exact, quote-close
 * - issues (red): contradicts, wrong_document, hallucination, quote-mismatch, unrelated
 * - unresolved (yellow/orange): blocked, paywalled, access_denied, bot_protected, not_found, 
 *   timeout, network_error, vague_citation, no_search_results, ambiguous, insufficient, error
 * 
 * @param {string} label - Badge label
 * @returns {string} - CSS class name
 */
export function badgeClass(label) {
  const base = 'badge';
  const norm = String(label || '').toLowerCase().replace(/-/g, '_');
  
  // VERIFIED: Successful verification outcomes (green)
  if (norm === 'supports') return `${base} supports`;
  if (norm === 'partial' || norm === 'weak/qualified') return `${base} partial`;
  if (norm === 'quote_exact') return `${base} supports`;
  if (norm === 'quote_close') return `${base} partial`;

  // ISSUES: Verification found problems (red)
  if (norm === 'disputes' || norm === 'refutes' || norm === 'contradicts' || norm === 'unsupported' || norm === 'neutral') return `${base} disputes`;
  if (norm === 'hallucination') return `${base} hallucination`;
  if (norm === 'unrelated') return `${base} disputes`;
  if (norm === 'mismatch') return `${base} disputes`;
  if (norm === 'quote_mismatch' || norm === 'quote_not_found') return `${base} disputes`;
  if (norm === 'quote_altered') return `${base} quote_altered`;
  // Stitched quote: words present but silently non-contiguous → flag (amber), not a hard fail
  if (norm === 'quote_stitched') return `${base} quote_altered`;
  // Misattributed quote: words faithful but are a party's position / another source presented
  // as the court's own holding → flag (amber), not a hard fail
  if (norm === 'quote_attribution') return `${base} quote_altered`;
  // Quote could not be verified verbatim (retrieval gap) → neutral/unresolved, not a fail
  if (norm === 'quote_unverified') return `${base} warn`;

  // RESOLUTION FAILURE: source resolved to wrong document → treat as unresolved (excluded from % score)
  if (norm === 'wrong_document') return `${base} warn`;

  // UNRESOLVED - Access Issues (orange - source exists but can't access)
  if (norm === 'paywalled') return `${base} paywalled`;
  if (norm === 'access_denied') return `${base} access_denied`;
  if (norm === 'bot_protected') return `${base} bot_protected`;
  if (norm === 'blocked') return `${base} blocked`;
  
  // UNRESOLVED - Not Found (orange-red)
  if (norm === 'not_found' || norm === 'unfound') return `${base} not_found`;
  
  // UNRESOLVED - Technical Issues (yellow)
  if (norm === 'timeout') return `${base} timeout`;
  if (norm === 'network_error') return `${base} network_error`;
  if (norm === 'error' || norm === 'failed') return `${base} error`;
  
  // UNRESOLVED - Resolution Incomplete (yellow)
  if (norm === 'vague_citation') return `${base} vague_citation`;
  if (norm === 'no_search_results') return `${base} no_search_results`;
  if (norm === 'ambiguous') return `${base} ambiguous`;
  if (norm === 'insufficient') return `${base} insufficient`;
  if (norm === 'mixed' || norm === 'unclear') return `${base} warn`;
  
  // Neutral - not actual citations
  if (norm === 'wet' || norm === 'comment') return `${base}`;
  
  // Web-related statuses (informational badges)
  if (norm === 'exists') return `${base} exists`;
  if (norm === 'free') return `${base} free`;
  
  // Default to warning (unresolved) for unknown labels
  return `${base} warn`;
}

// Quote verdicts that are a REAL misquote: the quoted words are not what the source
// says (mismatch), or are not in the source at all (not_found). Everything short of
// that — exact, close, stitched, unverified — leaves the citation Verified.
const QUOTE_ISSUE_LABELS = new Set(['quote_mismatch', 'quote_not_found']);

const normLabel = (v) => String(v || '').toLowerCase().replace(/-/g, '_');

/**
 * Detect a confirmed misquote on a result, wherever the quote signal happens to live
 * (reconciled quote_label, the quote_check object, or the congruence secondary labels).
 *
 * The congruence verdict and the quote verdict are INDEPENDENT: a source can genuinely
 * support the claim while the quotation of it is altered or absent. That citation belongs
 * in Issues — the misquote is the finding.
 *
 * @param {Object} data - Result data
 * @returns {string|null} - 'quote_mismatch' | 'quote_not_found', or null
 */
export function quoteIssueLabel(data) {
  if (!data || typeof data !== 'object') return null;

  const candidates = [];
  const push = (v) => { if (v) candidates.push(normLabel(v)); };

  push(data.quote_label);
  push(data.analyze_result?.quote_label);
  push(data.congruence_result?.quote_label);
  push(data.congruence?.quote_label);

  const quoteCheck = data.quote_check
    || data.quoteCheck
    || data.quote_result
    || data.analyze_result?.quote_check
    || data.congruence_result?.quote_check
    || data.congruence?.quote_check;
  if (quoteCheck && typeof quoteCheck === 'object' && quoteCheck.label) {
    push(`quote_${normLabel(quoteCheck.label)}`);
  }

  const secondary = data.secondary_labels
    || data.congruence_result?.secondary_labels
    || data.congruence?.secondary_labels
    || data.analyze_result?.congruence_result?.secondary_labels;
  if (Array.isArray(secondary)) secondary.forEach(push);

  return candidates.find((c) => QUOTE_ISSUE_LABELS.has(c)) || null;
}

/**
 * Map result data to status icon type
 * Four categories:
 * - 'ok' (✓): Verified - supports, partial, quote-exact, quote-close
 * - 'fail' (✕): Issues - contradicts, wrong_document, hallucination, unrelated
 * - 'quote' (❝): Issues - the claim is supported but the QUOTE is a misquote
 * - 'ask' (?): Unresolved - all resolution failures and incomplete states
 *
 * A misquote only ever demotes a Verified citation. It never promotes an Unresolved one:
 * wrong_document, say, has no matching quote by construction, and that is a resolution
 * problem, not a quotation one.
 *
 * @param {Object} data - Result data
 * @returns {string} - Status type: 'ok', 'fail', 'quote', or 'ask'
 */
export function mapResultToStatus(data) {
  const contribution = data?.joint_member_result?.contribution_label
    || data?.congruence?.joint_member_result?.contribution_label
    || data?.congruence_result?.joint_member_result?.contribution_label;
  if (contribution === 'contributes') return 'ok';
  if (contribution === 'does_not_contribute' || contribution === 'contradicts') return 'fail';
  if (contribution === 'non-verifiable') return 'ask';
  const base = baseResultStatus(data);
  if (base === 'ok' && quoteIssueLabel(data)) return 'quote';
  return base;
}

function baseResultStatus(data) {
  // Handle nested structure from VerifyAll mode
  const rawLabel = String(data?.label || data?.analyze_result?.label || data?.analyze_result?.congruence_result?.label || '');
  const label = rawLabel.toLowerCase().replace(/-/g, '_');
  const status = String(data?.status || '').toLowerCase();

  // VERIFIED (ok) - Successful verification
  if (['supports', 'partial', 'weak/qualified', 'quote_exact', 'quote_close'].includes(label)) return 'ok';

  // ISSUES (fail) - Verification found problems
  if ([
    'hallucination', 'unrelated', 'mismatch',
    'refutes', 'disputes', 'contradicts', 'unsupported', 'neutral',
    'quote_mismatch', 'quote_not_found'
  ].includes(label)) return 'fail';

  // UNRESOLVED (ask) - Resolution failed or incomplete
  // Resolved to wrong document (treat like failed resolution)
  if (label === 'wrong_document') return 'ask';
  // Access issues
  if (['blocked', 'paywalled', 'access_denied', 'bot_protected'].includes(label)) return 'ask';
  // Not found
  if (['not_found', 'unfound'].includes(label)) return 'ask';
  // Technical issues
  if (['timeout', 'network_error', 'error', 'failed'].includes(label)) return 'ask';
  // Resolution incomplete
  if (['vague_citation', 'no_search_results', 'ambiguous', 'insufficient'].includes(label)) return 'ask';
  // Other unresolved states
  if (status === 'unresolved' || label === 'unresolved' || (!data?.file && !label)) return 'ask';
  if (['wet', 'comment'].includes(label) || ['wet', 'comment'].includes(status)) return 'ask';
  if (['mixed', 'unclear'].includes(label)) return 'ask';

  // Default to unresolved for unknown labels
  return 'ask';
}

/**
 * Generate HTML for a status icon
 * @param {string} kind - Icon kind: 'ok', 'fail', or 'ask'
 * @param {string} title - Tooltip title
 * @returns {string} - HTML string
 */
export function iconHTML(kind, title) {
  if (kind === 'ok')    return `<span class="si ok"    title="${title}">✓</span>`;
  if (kind === 'fail')  return `<span class="si fail"  title="${title}">✕</span>`;
  // Misquote: a red quotation mark, so it reads as a quote problem at a glance rather
  // than as another failed check.
  if (kind === 'quote') return `<span class="si quote" title="${title}">❝</span>`;
  return `<span class="si ask"  title="${title}">"</span>`;
}

/**
 * Calculate a citation score based on result label and calibration.
 * Scoring formula:
 * - supports + calibration → calibration * 100%
 * - partial + calibration → calibration * 50%
 * - issues (hallucination, contradicts, etc.) → 0%
 * - resolution failures (e.g. wrong_document) → excluded from scoring (null score)
 * - unresolved → excluded from scoring (null score)
 *
 * @param {Object} data - Result data for a single citation
 * @returns {Object} { score: number|null (0-100, null if unresolved), category: 'verified'|'issues'|'unresolved' }
 */
export function calculateCitationScore(data) {
  if (!data) {
    return { score: null, category: 'unresolved' };
  }

  // A confirmed misquote is an Issue even when the source supports the claim, and it
  // scores 0: the citation misrepresents what it quotes. Only demotes a would-be
  // Verified result (see mapResultToStatus).
  const quoteIssue = quoteIssueLabel(data);
  if (quoteIssue) {
    const base = calculateBaseCitationScore(data);
    if (base.category === 'verified') return { score: 0, category: 'issues' };
    return base;
  }
  return calculateBaseCitationScore(data);
}

function calculateBaseCitationScore(data) {

  // Extract label from various possible locations
  const rawLabel = String(
    data?.label ||
    data?.congruence_result?.label ||
    data?.analyze_result?.label ||
    data?.analyze_result?.congruence_result?.label ||
    ''
  );
  const label = rawLabel.toLowerCase().replace(/-/g, '_');

  // Extract calibration (default to 1.0 if not present)
  const rawCalibration =
    data?.calibration ??
    data?.congruence_result?.calibration ??
    data?.analyze_result?.calibration ??
    data?.analyze_result?.congruence_result?.calibration ??
    1.0;
  const parsedCalibration = parseFloat(rawCalibration);
  const calibration = Number.isFinite(parsedCalibration)
    ? Math.max(0, Math.min(1, parsedCalibration))
    : 1.0;

  // RESOLUTION FAILURE - exclude from scoring
  // `wrong_document` means we likely resolved the wrong source, so it should not
  // reduce the "supported by sources (%)" metric (which is meant to reflect
  // successfully-evaluated sources).
  if (label === 'wrong_document') {
    return { score: null, category: 'unresolved' };
  }

  // VERIFIED - Full support
  if (label === 'supports' || label === 'quote_exact') {
    return { score: calibration * 100, category: 'verified' };
  }

  // VERIFIED - Partial support (50% weight)
  if (label === 'partial' || label === 'weak/qualified' || label === 'quote_close') {
    return { score: calibration * 50, category: 'verified' };
  }

  // INSUFFICIENT - Include it in the denominator because congruence was evaluated,
  // but confidence in an insufficient-evidence verdict is not evidence of support.
  if (label === 'insufficient') {
    return { score: 0, category: 'unresolved' };
  }

  // ADVERSE CONGRUENCE - Include it in the denominator, but it contributes zero
  // support. Calibration is confidence IN the adverse verdict, not a support score.
  if (['refutes', 'disputes', 'contradicts', 'unsupported', 'neutral'].includes(label)) {
    return { score: 0, category: 'issues' };
  }

  // ISSUES - Other verification problems (0%)
  if ([
    'hallucination', 'unrelated', 'mismatch',
    'quote_mismatch', 'quote_not_found', 'quote_altered'
  ].includes(label)) {
    return { score: 0, category: 'issues' };
  }

  // UNRESOLVED - Access issues, technical issues, incomplete resolution
  if ([
    'blocked', 'paywalled', 'access_denied', 'bot_protected',
    'not_found', 'unfound',
    'timeout', 'network_error', 'error', 'failed',
    'vague_citation', 'no_search_results', 'ambiguous',
    'mixed', 'unclear', 'wet', 'comment'
  ].includes(label)) {
    return { score: null, category: 'unresolved' };
  }

  // No label or unknown → treat as unresolved
  if (!label || label === 'unknown') {
    return { score: null, category: 'unresolved' };
  }

  // Default fallback for any other labels → unresolved
  return { score: null, category: 'unresolved' };
}
